import React, { createContext, useState, useEffect, useContext, SetStateAction } from 'react';
import CommonType from './types/CommonType';
import { NOTIFICATION_TYPES, NotificationContext } from './NotificationContext';
import { LoadingContext } from './LoadingContext';
import CountryType from './types/CountryType';
import { LocalStorageHelper } from '../utils/LocalStorageHelper';
import { AxiosResponse } from 'axios';
import logger from '../utils/logger';

const LOCAL_STORAGE_COUNTRIES = 'LOCAL_STORAGE_COUNTRIES';

export const CountryContext = createContext({
  countries: [] as CountryType[]
});

const CountryProvider: React.FC<CommonType> = props => {
  const [countries, setCountriesData] = useState<CountryType[]>([]);
  const { setType, setMessage } = useContext(NotificationContext);
  const { setLoading } = useContext(LoadingContext);

  useEffect(() => {
    logger.context.debug('Initializing country data');
    setLoading(true);
    const localCountries = LocalStorageHelper.getLocalResponseDataWithExpiration<CountryType[]>(LOCAL_STORAGE_COUNTRIES);

    if (localCountries !== null) {
      logger.context.debug('Using cached countries:', localCountries.data?.length, 'items');
      setCountriesData(localCountries.data);
      setLoading(false);
    } else {
      logger.context.debug('Loading countries from public API');
      // Use public services as countries should be publicly accessible
      import('../service/publicServices').then(({ default: publicServices }) => {
        publicServices.getCountries()
          .then((response: AxiosResponse<SetStateAction<any>>) => {
            logger.context.debug('Countries loaded successfully:', response.data?.length, 'items');
            setCountriesData(response.data);
            LocalStorageHelper.setLocalResponseDataWithExpiration(LOCAL_STORAGE_COUNTRIES, response);
          })
          .catch((error: { message: string; }) => {
            logger.context.error('Error loading countries:', error);
            setType(NOTIFICATION_TYPES.FAILED);
          setMessage(error.message);
          })
          .finally(() => {
            setLoading(false);
          });
      }).catch((importError) => {
        // Handle dynamic import error
        logger.context.error('Failed to load country services:', importError);
        setType(NOTIFICATION_TYPES.FAILED);
        setMessage('Failed to load countries');
        setLoading(false);
      });
    }
  }, []);

  return <CountryContext.Provider value={{ countries }}>{props.children}</CountryContext.Provider>;
};

export default CountryProvider;
